import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useGetWaivers from './useGetWaivers';
import { ROUTES } from '../../constants/global';

function useSelectWaiver() {
  const { waiverId } = useParams<{ waiverId: string | undefined }>();
  const navigate = useNavigate();

  const { data, isFetching } = useGetWaivers();

  const defaultWaiver = React.useMemo(
    () =>
      data?.options?.find(
        option => option.status === 'DRAFT' && option.isDefault,
      ),
    [data?.options],
  );

  const selectedWaiver = React.useMemo(
    () => data?.options?.find(option => option.waiverId === waiverId),
    [data?.options, waiverId],
  );

  const selectedPublishedWaiver = React.useMemo(() => {
    if (!selectedWaiver) return null;
    return data?.options?.find(
      option =>
        option.status === 'PUBLISHED' &&
        option.waiverName === selectedWaiver.waiverName,
    );
  }, [data?.options, selectedWaiver]);

  const onSelectWaiver = (
    newWaiverId: string | null,
    { isNew, submissions }: { isNew?: boolean; submissions?: boolean } = {},
  ) => {
    const searchParams = new URLSearchParams();

    if (submissions) {
      searchParams.set('tab', 'submissions');
    }

    const searchString =
      searchParams.toString().length > 0 ? `?${searchParams.toString()}` : '';

    if (!newWaiverId) {
      navigate(ROUTES.waivers);
      return;
    }

    // new waivers may not be in the options yet
    if (isNew) {
      navigate(`${ROUTES.waivers}/${newWaiverId}${searchString}`);
    } else {
      const newWaiver = data?.options?.find(
        option => option.waiverId === newWaiverId,
      );
      if (!newWaiver) return;
      const newDraftWaiver =
        newWaiver.status === 'DRAFT'
          ? newWaiver
          : data?.options?.find(
              option =>
                option.status === 'DRAFT' &&
                option.waiverName === newWaiver.waiverName,
            );
      navigate(
        `${ROUTES.waivers}/${
          newDraftWaiver?.waiverId || defaultWaiver?.waiverId || ''
        }${searchString}`,
      );
    }
  };

  React.useEffect(() => {
    if (isFetching || !data || !waiverId) return;
    const existingWaiver = data.options?.find(
      option => option.waiverId === waiverId,
    );
    if (!existingWaiver) {
      navigate(`${ROUTES.waivers}`);
    } else if (existingWaiver.status !== 'DRAFT') {
      const draftWaiver = data.options?.find(
        option =>
          option.status === 'DRAFT' &&
          option.waiverName === existingWaiver.waiverName,
      );
      if (!draftWaiver) {
        navigate(ROUTES.waivers);
      } else {
        navigate(`${ROUTES.waivers}/${draftWaiver?.waiverId}`);
      }
    }
  }, [data, isFetching, navigate, waiverId]);

  return {
    waiverId,
    waiver: selectedWaiver,
    publishedWaiver: selectedPublishedWaiver,
    defaultWaiver,
    onSelectWaiver,
  };
}

export default useSelectWaiver;
